/**
 * ContactCard Component
 * 
 * Displays a single contact in card format.
 */

import { Building2, Mail, Linkedin, Calendar, MoreVertical, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useJobHuntStore } from '@/stores/jobHuntStore';
import type { CompanyContact } from '@/types/job-hunt';
import { toast } from 'sonner';
import { formatDistance } from 'date-fns';

interface ContactCardProps {
  contact: CompanyContact;
  onEdit: () => void;
  onGenerateEmail: () => void;
}

export function ContactCard({ contact, onEdit, onGenerateEmail }: ContactCardProps) {
  const { deleteContact } = useJobHuntStore();

  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete this contact?')) {
      try {
        await deleteContact(contact.id);
        toast.success('Contact deleted');
      } catch (error) {
        toast.error('Failed to delete contact');
      }
    }
  };

  const needsFollowup = contact.next_followup_date && 
    new Date(contact.next_followup_date) <= new Date();

  return (
    <Card className={needsFollowup ? 'border-primary' : ''}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-3">
            {/* Header */}
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">{contact.contact_name}</h3>
                {contact.source && contact.source !== 'manual' && (
                  <Badge variant="outline" className="text-xs">
                    {contact.source === 'perplexity' && '🔍 Perplexity'}
                    {contact.source === 'apollo' && '🚀 Apollo'}
                    {contact.source === 'hybrid' && '⚡ Hybrid'}
                  </Badge>
                )}
              </div>
              {contact.contact_title && (
                <p className="text-sm text-muted-foreground">{contact.contact_title}</p>
              )}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                <Building2 className="h-4 w-4" />
                <span>{contact.company_name}</span>
              </div>
            </div>

            {/* Contact Info */}
            <div className="flex flex-wrap gap-3 text-sm">
              {contact.contact_email && (
                <a 
                  href={`mailto:${contact.contact_email}`}
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  <Mail className="h-3 w-3" />
                  {contact.contact_email}
                </a>
              )}
              {contact.contact_linkedin && (
                <a 
                  href={contact.contact_linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  <Linkedin className="h-3 w-3" />
                  LinkedIn
                </a>
              )}
            </div>

            {/* Tags */}
            {contact.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {contact.tags.map((tag) => (
                  <Badge key={tag} variant="outline">{tag}</Badge>
                ))}
              </div>
            )}

            {/* Notes */}
            {contact.relationship_notes && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {contact.relationship_notes}
              </p>
            )}

            {/* Follow-up Info */}
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2">
              {contact.last_contact_date && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>Last contact {formatDistance(new Date(contact.last_contact_date), new Date(), { addSuffix: true })}</span>
                </div>
              )}
              {contact.next_followup_date && (
                <div className={`flex items-center gap-1 ${needsFollowup ? 'text-primary font-medium' : ''}`}>
                  <Calendar className="h-3 w-3" />
                  <span>
                    {needsFollowup ? 'Follow-up due!' : `Follow-up ${formatDistance(new Date(contact.next_followup_date), new Date(), { addSuffix: true })}`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button onClick={onGenerateEmail} size="sm" className="w-full">
              <Sparkles className="h-3 w-3 mr-2" />
              Generate Email
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
